import { useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Grid, Link, Typography } from '@mui/material';
import { gql } from '__generated__/gql';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import { FunctionComponent, PropsWithChildren } from 'react';
import { FormattedNumberParts } from 'react-intl';
import { Link as RouterLink } from 'react-router';
import { ErrorMessage } from 'shared/ErrorMessage';
import { PartnersThemeDecider } from 'shared/PartnersThemeDecider';
import { PermissionsViewDecider } from 'shared/PermissionsViewDecider';
import { UserTeamAvatar } from 'shared/UserTeamAvatar';
import { APIPermission } from 'typeDeclarations/enums';
import { NavigationMenuHeaderQueryFragmentData } from './fragments';

type NavigationMenuHeaderQueryData = NavigationMenuHeaderQueryFragmentData;

const NAVIGATION_MENU_HEADER_QUERY = gql(/* GraphQL */ `
  query navigationMenuHeaderQuery {
    ...NavigationMenuHeaderQueryFragment
  }
`);

interface NavigationMenuHeaderProps {
  onAddMoneyClick: () => void;
  onLinkClick: () => void;
}

export const NavigationMenuHeader: FunctionComponent<PropsWithChildren<NavigationMenuHeaderProps>> = ({
  onAddMoneyClick,
  onLinkClick,
}) => {
  const { formatMessage } = useExtendedIntl();
  const { data, loading, error } = useQuery<NavigationMenuHeaderQueryData>(NAVIGATION_MENU_HEADER_QUERY);

  let teamNameTypography = null;
  let balanceContent = null;

  if (loading) {
    balanceContent = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error) {
    balanceContent = (
      <Typography align="center" color="inherit" variant="h4" component="p">
        <ErrorMessage />
      </Typography>
    );
  }

  if (data) {
    const { balance, currency, hasCreditLine, creditAvailable } = data.sessionTeam.billingProfile;

    let valueToFormat = 0;

    if (hasCreditLine && creditAvailable) {
      valueToFormat = Number(creditAvailable);
    } else if (balance) {
      valueToFormat = Number(balance);
    }

    teamNameTypography = (
      <Typography maxWidth="16rem" noWrap align="center">
        {data.sessionTeam.name}
      </Typography>
    );

    balanceContent = (
      <FormattedNumberParts value={valueToFormat} style="currency" currency={currency}>
        {(parts) => (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            {parts.map(({ type, value }, index) => {
              if (type === 'currency') {
                return (
                  <Typography
                    align="center"
                    color="inherit"
                    variant="body1"
                    component="span"
                    key={`balance-${type}-${value}-${index}`}
                  >
                    {value}
                  </Typography>
                );
              }
              return (
                <Typography
                  align="center"
                  color="inherit"
                  variant="h4"
                  component="span"
                  key={`balance-${type}-${value}-${index}`}
                >
                  {value}
                </Typography>
              );
            })}
          </Box>
        )}
      </FormattedNumberParts>
    );
  }

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      spacing={1}
      sx={{ marginTop: '1.25rem', marginBottom: '1rem' }}
    >
      <Grid container justifyContent="center">
        <UserTeamAvatar />
      </Grid>
      <Grid container direction="column" alignItems="center">
        <Grid>{teamNameTypography}</Grid>
        <Grid>
          <Link color="secondary" variant="caption" to="/team/dashboard" onClick={onLinkClick} component={RouterLink}>
            {formatMessage({ id: 'shared.overview' })}
          </Link>
        </Grid>
      </Grid>
      <PartnersThemeDecider
        // don't show the wallet and balance on fcr-be and yp-ca
        fcr-be={null}
        yp-ca={null}
        fallback={
          <>
            <Grid>{balanceContent}</Grid>
            <PermissionsViewDecider requiredPermissions={[APIPermission.PerformChanges]}>
              <Grid>
                <Button
                  variant="outlined"
                  onClick={onAddMoneyClick}
                  aria-label="top-up-wallet"
                  sx={{ minWidth: '6.5rem', display: 'flex', textAlign: 'center' }}
                >
                  {formatMessage({ id: 'navigation-menu-header.button-text' })}
                </Button>
              </Grid>
            </PermissionsViewDecider>
          </>
        }
      />
    </Grid>
  );
};
